import { Component } from '@theme/component';

/**
 * Multi-mode tracker for the Nouvel Heritage Meta intent funnel.
 *
 * Modes (set via data-mode):
 *
 *   pdp — fires on PDPs:
 *     - high_intent_visit   (35s dwell + 70% scroll + (3 products OR consideration page))
 *     - medium_intent_visit (20s dwell + 50% scroll + 2 distinct products in session)
 *     - multi_product_browse (3 distinct PDPs viewed in session, no dwell/scroll required)
 *     - returning_pdp_visit (current session ≠ first-ever PDP session — cross-session)
 *
 *   editorial — fires on blog / article / brand-story pages:
 *     - editorial_engaged   (2+ distinct editorial pages in session, no dwell/scroll required)
 *     Also sets the consideration flag for High Intent qualification.
 *
 *   logistics — fires on sizer / care / shipping / returns / FAQ / stores pages:
 *     - logistics_page_visit (any visit, deduped per page handle per session)
 *     Also sets the consideration flag.
 *
 *   collection — fires on collection pages:
 *     - collection_deep_browse (20s active dwell + 50% scroll + clicked into ≥ 1 PDP)
 *     Once per collection handle per session.
 *
 *   consideration — legacy compatibility mode. Just sets the consideration flag.
 *
 *   benchmark — fires on every page (rendered globally from layout/theme.liquid).
 *     Maps directly to the GA4 benchmark targets so each event count is a
 *     measurable proxy for hitting "good visitor" behavior. Fires once per
 *     session per threshold:
 *       - session_3_pages       (3rd pageview)
 *       - session_4_pages       (4th pageview)
 *       - session_5_pages       (5th pageview)
 *       - session_dwell_2m      (2 min cumulative active dwell)
 *       - session_dwell_2m30s   (2:30 cumulative active dwell)
 *       - session_dwell_3m      (3 min cumulative active dwell)
 *       - returning_qualified   (returning user + ≥60s dwell + ≥2 pages)
 *       - weekly_returner       (returning within 7 days of last visit)
 *       - add_to_cart_qualified (ATC + ≥30s session dwell, deduped per variant)
 *
 *     Cumulative dwell is persisted to sessionStorage on visibility change /
 *     pagehide so it carries across navigation.
 *
 * Delivery path:
 *   storefront (this file) -> window.Shopify.analytics.publish('<event>', payload)
 *     -> Shopify Customer Event sandbox (custom pixel in Admin -> Settings
 *        -> Customer events) -> fbq('trackCustom', '<EventName>', payload).
 *
 * Because the Facebook & Instagram sales channel data sharing is set to Maximum,
 * each event is automatically mirrored to Conversions API server-side with a
 * matching event_id, so Meta dedupes browser and server signals.
 *
 * All events are also pushed to window.dataLayer for GTM parity.
 */

// ---- sessionStorage keys (per-tab session, cleared on tab close) ----
const SESSION_VIEWED_KEY = 'nh_hiv_session_handles';            // distinct PDP handles viewed
const SESSION_HIGH_FIRED_KEY = 'nh_hiv_fired_handles';          // PDPs where HighIntent fired
const SESSION_MEDIUM_FIRED_KEY = 'nh_hiv_medium_fired_handles'; // PDPs where MediumIntent fired
const SESSION_CONSIDERATION_KEY = 'nh_hiv_consideration_visited';
const SESSION_MULTI_FIRED_KEY = 'nh_hiv_multi_product_fired';
const SESSION_RETURNING_FIRED_KEY = 'nh_hiv_returning_fired';
const SESSION_ID_KEY = 'nh_hiv_session_id';
const SESSION_EDITORIAL_KEY = 'nh_hiv_editorial_handles';       // distinct editorial pages visited
const SESSION_EDITORIAL_FIRED_KEY = 'nh_hiv_editorial_fired';
const SESSION_COLLECTION_FIRED_KEY = 'nh_hiv_collection_fired_handles';
const SESSION_LOGISTICS_FIRED_KEY = 'nh_hiv_logistics_fired_handles';
const SESSION_HIGH_VALUE_FIRED_KEY = 'nh_hiv_high_value_fired_handles';   // PDPs where ViewItemHighValue fired

// ---- Benchmark-mode sessionStorage keys (May 2026 batch) ----
const SESSION_PAGES_COUNT_KEY = 'nh_hiv_pages_count';                  // integer pageviews this session
const SESSION_PAGES_3_FIRED_KEY = 'nh_hiv_pages_3_fired';              // session_3_pages fired flag
const SESSION_PAGES_4_FIRED_KEY = 'nh_hiv_pages_4_fired';              // session_4_pages fired flag
const SESSION_PAGES_5_FIRED_KEY = 'nh_hiv_pages_5_fired';              // session_5_pages fired flag
const SESSION_DWELL_TOTAL_KEY = 'nh_hiv_session_dwell_total_ms';       // cumulative active dwell across pages
const SESSION_DWELL_2M_FIRED_KEY = 'nh_hiv_dwell_2m_fired';            // session_dwell_2m fired flag
const SESSION_DWELL_2M30S_FIRED_KEY = 'nh_hiv_dwell_2m30s_fired';      // session_dwell_2m30s fired flag
const SESSION_DWELL_3M_FIRED_KEY = 'nh_hiv_dwell_3m_fired';            // session_dwell_3m fired flag
const SESSION_RETURNING_QUALIFIED_FIRED_KEY = 'nh_hiv_returning_qualified_fired';
const SESSION_ATC_QUALIFIED_FIRED_KEY = 'nh_hiv_atc_qualified_fired_variants'; // list of variantIds

// ---- localStorage keys (persistent across sessions) ----
const LOCAL_FIRST_PDP_KEY = 'nh_hiv_first_pdp_session';
const LOCAL_LAST_VISIT_TS_KEY = 'nh_hiv_last_visit_ts';                // most-recent visit timestamp
const LOCAL_WEEKLY_RETURNER_LAST_KEY = 'nh_hiv_weekly_returner_last_ts'; // last weekly_returner fire ts

// ---- Storage helpers ----
function safeGet(storage, key) {
  try {
    return storage.getItem(key);
  } catch (_) {
    return null;
  }
}

/**
 * Analytics-consent gate for everything this module stores or emits.
 *
 * WHY: this tracker writes nh_hiv_* keys and publishes intent events on every
 * page view. Verified live 2026-07-30: it kept doing both after a visitor
 * chose "Decline all", with analytics consent recorded as "no" — first-party,
 * but still behavioural analytics collected against an explicit refusal.
 *
 * Fails CLOSED, deliberately: NH's stance (Camille, 2026-07-30) is that
 * nothing non-essential runs until the visitor agrees, in every region — not
 * only where a regulation forces it. Note this differs from
 * pinterest-intent-relay.js, which still fails open when the consent API is
 * absent; that asymmetry is intentional for now and tracked as a follow-up.
 *
 * Consent is re-read on every call rather than cached, so accepting mid-visit
 * starts collection immediately (no retroactive backfill — events and dwell
 * from before acceptance are simply not recorded).
 */
/**
 * Prior-consent (opt-in) versus opt-out jurisdictions.
 *
 * MUST STAY IDENTICAL to OPT_OUT_COUNTRY_PREFIXES in assets/nh-consent-banner.js
 * and OPT_OUT_COUNTRIES in snippets/nh-consent-default-deny.liquid. That is three
 * copies of the same list. If they drift, this gate disagrees with the banner
 * about which visitors were ever going to be asked, and the symptom is silent
 * under-collection rather than an error.
 *
 * Fails closed: unknown or unreadable region means prior consent is required.
 */
const OPT_OUT_COUNTRY_PREFIXES = ['US'];

function requiresPriorConsent(privacy) {
  if (!privacy || typeof privacy.getRegion !== 'function') return true;
  let region;
  try {
    region = privacy.getRegion();
  } catch (_) {
    return true;
  }
  if (typeof region !== 'string' || region.length < 2) return true;
  return !OPT_OUT_COUNTRY_PREFIXES.includes(region.slice(0, 2).toUpperCase());
}

function analyticsConsentGranted() {
  const privacy = window.Shopify && window.Shopify.customerPrivacy;
  if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') return false;

  // OPT-IN REGIONS ONLY: require an ACTUAL recorded choice first. Checking
  // analyticsProcessingAllowed() alone is not enough there, because this module
  // is not guaranteed to run after nh-consent-banner.js, and until that banner
  // writes its default refusal Shopify reports "yes" for every purpose. That
  // race is not theoretical — verified live 2026-07-30, where a first page view
  // still wrote nh_hiv_session_dwell_total_ms before the refusal landed. The
  // nh_cc marker is written synchronously on click, so its presence is the
  // earliest reliable proof the visitor actually decided.
  //
  // OPT-OUT REGIONS (currently US only): the marker is NOT required, and must
  // not be. Two reasons, one legal and one measured.
  //
  //   Legal: CCPA and the US state laws give a right to OPT OUT. Collection
  //   before a choice is lawful provided notice, a working opt-out and GPC are
  //   in place — all three are. Requiring an affirmative click would treat
  //   "ignored the banner" as "refused", which is neither what the law says nor
  //   what NH means. A real refusal still blocks everything, because
  //   analyticsProcessingAllowed() flips to false the moment Decline is clicked.
  //
  //   Measured: with the marker required, this gate cost roughly two thirds of
  //   the intent funnel. Load-time events retained ~3% of baseline and interval
  //   events ~35% (GA4, 2026-07-31 onward), and cost per MediumIntentVisit went
  //   from about $30 to $100. The mechanism is in the connectedCallback note
  //   below: the session product list is written at load time, ~200ms in, long
  //   before anyone reads a banner, so the 2- and 3-product thresholds could
  //   never be met however long a US visitor stayed.
  //
  //   There is also no race to protect against here: applyDefaultDenyUntilChoice()
  //   in nh-consent-banner.js returns early for opt-out regions, so no default
  //   refusal is ever written and Shopify's "yes" is the correct answer, not a
  //   transient one.
  //
  // Fails closed either way: no privacy API means no collection.
  if (requiresPriorConsent(privacy)) {
    if (!document.cookie.split(';').some((part) => part.trim().startsWith('nh_cc='))) return false;
  }

  try {
    return privacy.analyticsProcessingAllowed() === true;
  } catch (_) {
    return false;
  }
}

function safeSet(storage, key, value) {
  if (!analyticsConsentGranted()) return;
  try {
    storage.setItem(key, value);
  } catch (_) {
    // Private browsing / quota exceeded — fail silently
  }
}

function getSessionList(key) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : [];
  } catch (_) {
    return [];
  }
}

function setSessionList(key, list) {
  safeSet(sessionStorage, key, JSON.stringify(list));
}

function appendUniqueToSessionList(key, value) {
  const list = getSessionList(key);
  if (!list.includes(value)) {
    list.push(value);
    setSessionList(key, list);
  }
  return list;
}

function ensureSessionId() {
  let id = safeGet(sessionStorage, SESSION_ID_KEY);
  if (!id) {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      id = crypto.randomUUID();
    } else {
      id = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }
    safeSet(sessionStorage, SESSION_ID_KEY, id);
  }
  return id;
}

// ---- Publisher: emits to Shopify analytics + dataLayer ----
function publishEvent(eventName, payload) {
  // Consent gate — see analyticsConsentGranted(). Blocked events are dropped,
  // not queued: an intent signal is only meaningful in the session that
  // produced it, and replaying a refused visitor's browsing after they later
  // accept would defeat the point of asking.
  if (!analyticsConsentGranted()) return;
  if (
    window.Shopify &&
    window.Shopify.analytics &&
    typeof window.Shopify.analytics.publish === 'function'
  ) {
    window.Shopify.analytics.publish(eventName, payload);
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: eventName, ...payload });

  // Same-page relay for main-window listeners (e.g. pinterest-intent-relay).
  // Events are also buffered so a relay that loads after this event still
  // receives it — the shared object reference lets listeners dedupe.
  const detail = { event: eventName, payload };
  window.__nhIntentEventBuffer = window.__nhIntentEventBuffer || [];
  window.__nhIntentEventBuffer.push(detail);
  window.dispatchEvent(new CustomEvent('nh:intent-event', { detail }));
}

class HighIntentVisitTracking extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.initMode();

    // If the visitor had not answered the consent banner when this page loaded,
    // every storage write and event publish in initMode() above was dropped by
    // the gate — so the page they are on right now is invisible to us. Re-run
    // once, and only once, if a real choice arrives during this page view.
    //
    // This is what kept the load-time events near zero from 2026-07-31. They
    // fire synchronously here, ~200ms into the page, long before anyone has read
    // the banner — so they were always dropped and never retried, while events
    // on the 1s interval (session_dwell_*, collection_deep_browse) came through
    // fine for the same visitors once they accepted. Confirmed against GA4:
    // load-time events retained ~3% of baseline, interval events ~35%.
    //
    // Also unblocks medium_intent_visit / high_intent_visit: their thresholds
    // read the session product list, which is written at load time, so a dropped
    // write left the list empty for the whole visit and the 2- and 3-product
    // conditions could never be met however long the visitor stayed.
    //
    // No backfill: dwell accrued before acceptance stays discarded, consistent
    // with the stance documented in analyticsConsentGranted().
    if (!analyticsConsentGranted()) this.watchForConsentGrant();
  }

  /** Mode dispatch, factored out so it can be re-run on a consent grant. */
  initMode() {
    const mode = this.dataset.mode || 'pdp';

    switch (mode) {
      case 'pdp':
        this.initPdp();
        break;
      case 'editorial':
        this.initEditorial();
        break;
      case 'logistics':
        this.initLogistics();
        break;
      case 'collection':
        this.initCollection();
        break;
      case 'consideration':
        // Legacy compat — just flag the session
        safeSet(sessionStorage, SESSION_CONSIDERATION_KEY, '1');
        break;
      case 'benchmark':
        this.initBenchmark();
        break;
    }
  }

  /**
   * Listen for a consent choice landing mid-page-view.
   *
   * Gated on analyticsConsentGranted() rather than on the event itself:
   * `visitorConsentCollected` also fires for our own programmatic writes — the
   * inline default-deny in snippets/nh-consent-default-deny.liquid, the GPC
   * sale-of-data opt-out, and the banner's self-heal. None of those write an
   * nh_cc marker, so the gate stays closed and they are correctly ignored here.
   * That makes the __nhConsentSuppressCollectedUntil handshake used in
   * assets/nh-consent-banner.js unnecessary for this listener.
   */
  watchForConsentGrant() {
    this.handleConsentGrant = () => {
      if (this.consentReinitDone) return;
      if (!analyticsConsentGranted()) return;

      this.consentReinitDone = true;
      this.stopWatchingForConsentGrant();

      // Drop the listeners and intervals left behind by the first, ungated pass
      // before starting clean, or scroll/visibility/click handlers double up.
      this.teardown();
      this.initMode();
    };

    document.addEventListener('visitorConsentCollected', this.handleConsentGrant);
    document.addEventListener('nh:consent-choice', this.handleConsentGrant);
  }

  stopWatchingForConsentGrant() {
    if (!this.handleConsentGrant) return;
    document.removeEventListener('visitorConsentCollected', this.handleConsentGrant);
    document.removeEventListener('nh:consent-choice', this.handleConsentGrant);
    this.handleConsentGrant = null;
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.stopWatchingForConsentGrant();
    this.teardown();
  }

  teardown() {
    if (this.handleScroll) {
      window.removeEventListener('scroll', this.handleScroll);
    }
    if (this.handleVisibility) {
      document.removeEventListener('visibilitychange', this.handleVisibility);
    }
    if (this.handlePdpClick) {
      document.removeEventListener('click', this.handlePdpClick, { capture: true });
    }
    if (this.handleCartAdd) {
      document.removeEventListener('cart:update', this.handleCartAdd);
      this.handleCartAdd = null;
    }
    if (this.handlePageHide) {
      window.removeEventListener('pagehide', this.handlePageHide);
      this.handlePageHide = null;
    }
    if (this.handleBenchmarkVisibility) {
      document.removeEventListener('visibilitychange', this.handleBenchmarkVisibility);
      this.handleBenchmarkVisibility = null;
    }
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.benchmarkIntervalId) {
      clearInterval(this.benchmarkIntervalId);
      this.benchmarkIntervalId = null;
    }
  }

  // ---- Shared dwell + visibility tracking ----

  setupDwellTracking() {
    this.dwellStart = Date.now();
    this.dwellElapsed = 0;
    this.visible = !document.hidden;
    this.handleVisibility = () => {
      if (document.hidden) {
        if (this.visible) this.dwellElapsed += Date.now() - this.dwellStart;
        this.visible = false;
      } else {
        this.dwellStart = Date.now();
        this.visible = true;
      }
    };
    document.addEventListener('visibilitychange', this.handleVisibility);
  }

  activeDwellMs() {
    if (this.visible) return this.dwellElapsed + (Date.now() - this.dwellStart);
    return this.dwellElapsed;
  }

  setupScrollTracking(onScroll) {
    this.maxScrollPct = 0;
    this.handleScroll = () => {
      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop || 0;
      const windowHeight = window.innerHeight || doc.clientHeight;
      const docHeight = doc.scrollHeight || 0;
      if (docHeight <= windowHeight) {
        this.maxScrollPct = 100;
      } else {
        const pct = Math.min(100, Math.round(((scrollTop + windowHeight) / docHeight) * 100));
        if (pct > this.maxScrollPct) this.maxScrollPct = pct;
      }
      if (onScroll) onScroll();
    };
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    this.handleScroll();
  }

  considerationVisited() {
    return safeGet(sessionStorage, SESSION_CONSIDERATION_KEY) === '1';
  }

  // ============================================================
  // PDP MODE
  // ============================================================

  initPdp() {
    this.productId = this.dataset.productId;
    this.productHandle = this.dataset.productHandle;
    this.productTitle = this.dataset.productTitle;
    this.productPrice = parseFloat(this.dataset.productPrice) || 0;
    this.currency = this.dataset.currency || 'USD';

    // High Intent thresholds (Tier 1 — existing)
    this.highDwellMs = (parseInt(this.dataset.highDwellThreshold, 10) || 35) * 1000;
    this.highScrollPct = parseInt(this.dataset.highScrollThreshold, 10) || 70;
    this.highMinProducts = parseInt(this.dataset.highMinProducts, 10) || 3;

    // Medium Intent thresholds (Tier 2 — new)
    this.mediumDwellMs = (parseInt(this.dataset.mediumDwellThreshold, 10) || 20) * 1000;
    this.mediumScrollPct = parseInt(this.dataset.mediumScrollThreshold, 10) || 50;
    this.mediumMinProducts = parseInt(this.dataset.mediumMinProducts, 10) || 2;

    // Multi-Product Browse threshold (no dwell/scroll requirement)
    this.multiProductMin = parseInt(this.dataset.multiProductMin, 10) || 3;

    // 1. Register this PDP view in the session-wide set
    const viewedList = appendUniqueToSessionList(SESSION_VIEWED_KEY, this.productHandle);

    // 2. Returning Visitor on PDP — cross-session check
    this.checkReturningVisitor();

    // 3. Multi-Product Browse — fires once per session at >= 3 distinct PDPs
    this.checkMultiProductBrowse(viewedList);

    // 4. View Item High Value — fires once per handle per session for PDPs >= $3k
    this.checkHighValueProductView();

    // 5. Tiered intent (High + Medium) — set up dwell + scroll tracking
    this.highFired = getSessionList(SESSION_HIGH_FIRED_KEY).includes(this.productHandle);
    this.mediumFired = getSessionList(SESSION_MEDIUM_FIRED_KEY).includes(this.productHandle);

    if (this.highFired && this.mediumFired) {
      // Nothing more to track on this PDP this session
      return;
    }

    this.setupDwellTracking();
    this.setupScrollTracking(() => this.checkPdpQualified());
    this.intervalId = setInterval(() => {
      if (document.hidden) return;
      this.checkPdpQualified();
    }, 1000);
  }

  checkReturningVisitor() {
    const sessionId = ensureSessionId();
    const stored = safeGet(localStorage, LOCAL_FIRST_PDP_KEY);

    if (!stored) {
      // First-ever PDP visit — record this session as the "first PDP session"
      safeSet(
        localStorage,
        LOCAL_FIRST_PDP_KEY,
        JSON.stringify({ sessionId, ts: Date.now() })
      );
      return;
    }

    let data;
    try {
      data = JSON.parse(stored);
    } catch (_) {
      return;
    }

    if (data.sessionId === sessionId) {
      // Same session as their first-ever PDP visit — not a return yet
      return;
    }

    // Different session than the first PDP visit → returning visitor
    if (safeGet(sessionStorage, SESSION_RETURNING_FIRED_KEY) === '1') return;
    safeSet(sessionStorage, SESSION_RETURNING_FIRED_KEY, '1');

    publishEvent('returning_pdp_visit', {
      content_ids: [this.productHandle],
      content_type: 'product',
      content_name: this.productTitle,
      value: this.productPrice,
      currency: this.currency,
      trigger_handle: this.productHandle,
      first_visit_ts: data.ts || null,
    });
  }

  checkMultiProductBrowse(viewedList) {
    if (viewedList.length < this.multiProductMin) return;
    if (safeGet(sessionStorage, SESSION_MULTI_FIRED_KEY) === '1') return;
    safeSet(sessionStorage, SESSION_MULTI_FIRED_KEY, '1');

    publishEvent('multi_product_browse', {
      content_type: 'product',
      content_ids: viewedList,
      content_name: this.productTitle,
      num_products_viewed: viewedList.length,
      trigger_handle: this.productHandle,
      value: this.productPrice,
      currency: this.currency,
    });
  }

  // High-value product viewed — fires once per handle per session.
  // Buckets: $3k–$5k, $5k–$10k, $10k+. Below $3k does not fire.
  // Used to build GA4 audiences of luxury-tier shoppers.
  priceTier(price) {
    if (price >= 10000) return 'tier_10k_plus';
    if (price >= 5000) return 'tier_5k_10k';
    if (price >= 3000) return 'tier_3k_5k';
    return null;
  }

  checkHighValueProductView() {
    const tier = this.priceTier(this.productPrice);
    if (!tier) return;

    const fired = getSessionList(SESSION_HIGH_VALUE_FIRED_KEY);
    if (fired.includes(this.productHandle)) return;
    appendUniqueToSessionList(SESSION_HIGH_VALUE_FIRED_KEY, this.productHandle);

    publishEvent('view_item_high_value', {
      content_ids: [this.productHandle],
      content_type: 'product',
      content_name: this.productTitle,
      value: this.productPrice,
      currency: this.currency,
      price_tier: tier,
    });
  }

  checkPdpQualified() {
    const dwell = this.activeDwellMs();
    const scroll = this.maxScrollPct;
    const productsViewed = getSessionList(SESSION_VIEWED_KEY).length;

    // ---- Medium Intent (Tier 2) ----
    if (
      !this.mediumFired &&
      dwell >= this.mediumDwellMs &&
      scroll >= this.mediumScrollPct &&
      productsViewed >= this.mediumMinProducts
    ) {
      this.mediumFired = true;
      appendUniqueToSessionList(SESSION_MEDIUM_FIRED_KEY, this.productHandle);
      publishEvent('medium_intent_visit', {
        content_ids: [this.productHandle],
        content_type: 'product',
        content_name: this.productTitle,
        value: this.productPrice,
        currency: this.currency,
        num_products_viewed: productsViewed,
        dwell_seconds: Math.round(dwell / 1000),
        scroll_pct: scroll,
        intent_tier: 'medium',
      });
    }

    // ---- High Intent (Tier 1 — existing) ----
    if (
      !this.highFired &&
      dwell >= this.highDwellMs &&
      scroll >= this.highScrollPct
    ) {
      const productsQualified = productsViewed >= this.highMinProducts;
      const considerationQualified = this.considerationVisited();
      if (productsQualified || considerationQualified) {
        let reason = 'products_threshold';
        if (productsQualified && considerationQualified) reason = 'both';
        else if (considerationQualified) reason = 'consideration_page';

        this.highFired = true;
        appendUniqueToSessionList(SESSION_HIGH_FIRED_KEY, this.productHandle);
        publishEvent('high_intent_visit', {
          content_ids: [this.productHandle],
          content_type: 'product',
          content_name: this.productTitle,
          value: this.productPrice,
          currency: this.currency,
          num_products_viewed: productsViewed,
          dwell_seconds: Math.round(dwell / 1000),
          scroll_pct: scroll,
          qualification_reason: reason,
          intent_tier: 'high',
        });
      }
    }

    // Tear down once both have fired (or both already had on entry)
    if (this.highFired && this.mediumFired) this.teardown();
  }

  // ============================================================
  // EDITORIAL MODE
  // ============================================================

  initEditorial() {
    // Editorial pages also set the consideration flag (preserves High Intent path)
    safeSet(sessionStorage, SESSION_CONSIDERATION_KEY, '1');

    const handle = this.dataset.pageHandle;
    if (!handle) return;

    const list = appendUniqueToSessionList(SESSION_EDITORIAL_KEY, handle);
    const minPages = parseInt(this.dataset.editorialMin, 10) || 2;

    if (list.length < minPages) return;
    if (safeGet(sessionStorage, SESSION_EDITORIAL_FIRED_KEY) === '1') return;

    safeSet(sessionStorage, SESSION_EDITORIAL_FIRED_KEY, '1');

    publishEvent('editorial_engaged', {
      num_editorial_views: list.length,
      trigger_handle: handle,
      editorial_handles: list,
      page_type: this.dataset.editorialType || 'editorial',
    });
  }

  // ============================================================
  // LOGISTICS MODE
  // ============================================================

  initLogistics() {
    safeSet(sessionStorage, SESSION_CONSIDERATION_KEY, '1');

    const handle = this.dataset.pageHandle;
    if (!handle) return;

    const fired = getSessionList(SESSION_LOGISTICS_FIRED_KEY);
    if (fired.includes(handle)) return;
    fired.push(handle);
    setSessionList(SESSION_LOGISTICS_FIRED_KEY, fired);

    publishEvent('logistics_page_visit', {
      page_handle: handle,
      page_type: this.dataset.logisticsType || 'logistics',
    });
  }

  // ============================================================
  // COLLECTION MODE
  // ============================================================

  initCollection() {
    this.collectionHandle = this.dataset.collectionHandle;
    this.collectionTitle = this.dataset.collectionTitle || '';
    if (!this.collectionHandle) return;

    const fired = getSessionList(SESSION_COLLECTION_FIRED_KEY);
    if (fired.includes(this.collectionHandle)) return;

    this.collectionDwellMs = (parseInt(this.dataset.collectionDwellThreshold, 10) || 20) * 1000;
    this.collectionScrollPct = parseInt(this.dataset.collectionScrollThreshold, 10) || 50;
    this.pdpClicked = false;
    this.collectionFired = false;

    this.setupDwellTracking();
    this.setupScrollTracking(() => this.checkCollectionQualified());

    this.handlePdpClick = (e) => {
      const link = e.target.closest && e.target.closest('a[href*="/products/"]');
      if (!link) return;
      // Only count clicks that originate from inside this page (not e.g. nav menus)
      // Heuristic: link must be inside <main> if available, otherwise any product link counts
      const main = document.querySelector('main');
      if (main && !main.contains(link)) return;
      this.pdpClicked = true;
      this.checkCollectionQualified();
    };
    document.addEventListener('click', this.handlePdpClick, { capture: true });

    this.intervalId = setInterval(() => {
      if (document.hidden) return;
      this.checkCollectionQualified();
    }, 1000);
  }

  checkCollectionQualified() {
    if (this.collectionFired) return;
    if (!this.pdpClicked) return;
    if (this.activeDwellMs() < this.collectionDwellMs) return;
    if (this.maxScrollPct < this.collectionScrollPct) return;

    this.collectionFired = true;
    appendUniqueToSessionList(SESSION_COLLECTION_FIRED_KEY, this.collectionHandle);

    publishEvent('collection_deep_browse', {
      collection_handle: this.collectionHandle,
      collection_title: this.collectionTitle,
      dwell_seconds: Math.round(this.activeDwellMs() / 1000),
      scroll_pct: this.maxScrollPct,
    });

    this.teardown();
  }

  // ============================================================
  // BENCHMARK MODE — runs on every page
  // ============================================================
  // Fires session-mechanics events that map 1:1 to GA4 benchmark targets,
  // plus add_to_cart_qualified (filters misclick ATCs by requiring 30s of
  // accumulated session dwell). Designed to run alongside the page-specific
  // mode element on PDP / collection / editorial / logistics pages.

  initBenchmark() {
    ensureSessionId();

    // 1. Pages-per-session counter — increment once per page load
    this.bumpPageCount();

    // 2. Cross-page dwell tracking — restore total, set up per-page tracking
    this.benchmarkBaseDwellMs = parseInt(safeGet(sessionStorage, SESSION_DWELL_TOTAL_KEY) || '0', 10) || 0;
    this.setupBenchmarkDwell();

    // 3. Returning visitor checks (cross-session, time-based)
    this.checkWeeklyReturner();
    this.checkReturningQualified();

    // 4. ATC qualified — listen for cart:update events at document level
    this.handleCartAdd = (e) => this.onCartAdd(e);
    document.addEventListener('cart:update', this.handleCartAdd);

    // 5. Periodic check for dwell tier crossings + returning_qualified
    this.benchmarkIntervalId = setInterval(() => {
      if (document.hidden) return;
      this.checkDwellTiers();
      this.checkReturningQualified();
      this.persistDwellTotal();
      if (this.benchmarkChecksDone()) {
        clearInterval(this.benchmarkIntervalId);
        this.benchmarkIntervalId = null;
      }
    }, 1000);

    // 6. Persist dwell on pagehide so it carries across navigation
    this.handlePageHide = () => this.persistDwellTotal();
    window.addEventListener('pagehide', this.handlePageHide);

    // Also persist on visibility change so closed-tab cases capture dwell
    this.handleBenchmarkVisibility = () => {
      if (document.hidden) this.persistDwellTotal();
    };
    document.addEventListener('visibilitychange', this.handleBenchmarkVisibility);

    // Update last-visit timestamp on every page load (used by weekly_returner)
    safeSet(localStorage, LOCAL_LAST_VISIT_TS_KEY, String(Date.now()));
  }

  setupBenchmarkDwell() {
    // Reuses the shared dwell tracker pattern from setupDwellTracking() but
    // keeps a separate handler so PDP mode (which also uses dwell) doesn't
    // collide on the visibility listener.
    this.benchmarkDwellStart = Date.now();
    this.benchmarkDwellElapsed = 0;
    this.benchmarkVisible = !document.hidden;
    this.handleVisibility = () => {
      if (document.hidden) {
        if (this.benchmarkVisible) {
          this.benchmarkDwellElapsed += Date.now() - this.benchmarkDwellStart;
        }
        this.benchmarkVisible = false;
      } else {
        this.benchmarkDwellStart = Date.now();
        this.benchmarkVisible = true;
      }
    };
    document.addEventListener('visibilitychange', this.handleVisibility);
  }

  benchmarkActiveDwellMs() {
    if (this.benchmarkVisible) {
      return this.benchmarkDwellElapsed + (Date.now() - this.benchmarkDwellStart);
    }
    return this.benchmarkDwellElapsed;
  }

  totalSessionDwellMs() {
    return this.benchmarkBaseDwellMs + this.benchmarkActiveDwellMs();
  }

  benchmarkChecksDone() {
    return [
      SESSION_DWELL_2M_FIRED_KEY,
      SESSION_DWELL_2M30S_FIRED_KEY,
      SESSION_DWELL_3M_FIRED_KEY,
      SESSION_RETURNING_QUALIFIED_FIRED_KEY,
    ].every((key) => safeGet(sessionStorage, key) === '1');
  }

  persistDwellTotal() {
    safeSet(sessionStorage, SESSION_DWELL_TOTAL_KEY, String(this.totalSessionDwellMs()));
  }

  // ---- Pages-per-session ----

  bumpPageCount() {
    const current = parseInt(safeGet(sessionStorage, SESSION_PAGES_COUNT_KEY) || '0', 10) || 0;
    const next = current + 1;
    safeSet(sessionStorage, SESSION_PAGES_COUNT_KEY, String(next));

    if (next === 3 && safeGet(sessionStorage, SESSION_PAGES_3_FIRED_KEY) !== '1') {
      safeSet(sessionStorage, SESSION_PAGES_3_FIRED_KEY, '1');
      publishEvent('session_3_pages', { pages_in_session: 3, page_path: window.location.pathname });
    }
    if (next === 4 && safeGet(sessionStorage, SESSION_PAGES_4_FIRED_KEY) !== '1') {
      safeSet(sessionStorage, SESSION_PAGES_4_FIRED_KEY, '1');
      publishEvent('session_4_pages', { pages_in_session: 4, page_path: window.location.pathname });
    }
    if (next === 5 && safeGet(sessionStorage, SESSION_PAGES_5_FIRED_KEY) !== '1') {
      safeSet(sessionStorage, SESSION_PAGES_5_FIRED_KEY, '1');
      publishEvent('session_5_pages', { pages_in_session: 5, page_path: window.location.pathname });
    }
  }

  // ---- Dwell tier checks ----

  checkDwellTiers() {
    const total = this.totalSessionDwellMs();

    if (total >= 120000 && safeGet(sessionStorage, SESSION_DWELL_2M_FIRED_KEY) !== '1') {
      safeSet(sessionStorage, SESSION_DWELL_2M_FIRED_KEY, '1');
      publishEvent('session_dwell_2m', { dwell_seconds: Math.round(total / 1000), tier: '2m' });
    }
    if (total >= 150000 && safeGet(sessionStorage, SESSION_DWELL_2M30S_FIRED_KEY) !== '1') {
      safeSet(sessionStorage, SESSION_DWELL_2M30S_FIRED_KEY, '1');
      publishEvent('session_dwell_2m30s', { dwell_seconds: Math.round(total / 1000), tier: '2m30s' });
    }
    if (total >= 180000 && safeGet(sessionStorage, SESSION_DWELL_3M_FIRED_KEY) !== '1') {
      safeSet(sessionStorage, SESSION_DWELL_3M_FIRED_KEY, '1');
      publishEvent('session_dwell_3m', { dwell_seconds: Math.round(total / 1000), tier: '3m' });
    }
  }

  // ---- Returning visitor (qualified) ----
  // Fires once per session when a returning user has spent ≥60s cumulative
  // dwell AND viewed ≥2 pages. Filters out bounce-back returners.

  checkReturningQualified() {
    if (safeGet(sessionStorage, SESSION_RETURNING_QUALIFIED_FIRED_KEY) === '1') return;

    // Only fires for returning users (using the same first-PDP logic as
    // returning_pdp_visit, but expanded to any returning visit).
    const stored = safeGet(localStorage, LOCAL_FIRST_PDP_KEY);
    if (!stored) return; // no prior visit recorded

    let data;
    try {
      data = JSON.parse(stored);
    } catch (_) {
      return;
    }
    const sessionId = ensureSessionId();
    if (data.sessionId === sessionId) return; // same session as first ever

    const totalDwell = this.totalSessionDwellMs();
    const pages = parseInt(safeGet(sessionStorage, SESSION_PAGES_COUNT_KEY) || '0', 10) || 0;

    if (totalDwell < 60000 || pages < 2) return;

    safeSet(sessionStorage, SESSION_RETURNING_QUALIFIED_FIRED_KEY, '1');
    publishEvent('returning_qualified', {
      dwell_seconds: Math.round(totalDwell / 1000),
      pages_in_session: pages,
      first_visit_ts: data.ts || null,
    });
  }

  // ---- Weekly returner ----
  // Fires once per ≥7-day return window. Reads last-visit ts BEFORE we
  // overwrite it in initBenchmark — but since initBenchmark writes after
  // checkWeeklyReturner is called, the read here captures the prior visit.

  checkWeeklyReturner() {
    const lastVisitStr = safeGet(localStorage, LOCAL_LAST_VISIT_TS_KEY);
    if (!lastVisitStr) return; // first-ever visit — not a returner

    const lastVisit = parseInt(lastVisitStr, 10);
    if (!lastVisit) return;

    const now = Date.now();
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    const daysSince = (now - lastVisit) / (24 * 60 * 60 * 1000);

    // Must be a meaningful gap (>30 min) since last activity to count as a
    // new visit, AND ≤7 days to count as a "weekly" returner.
    if (now - lastVisit < 30 * 60 * 1000) return;
    if (now - lastVisit > sevenDaysMs) return;

    // Dedupe: only fire once per ≥7-day window
    const lastFireStr = safeGet(localStorage, LOCAL_WEEKLY_RETURNER_LAST_KEY);
    if (lastFireStr) {
      const lastFire = parseInt(lastFireStr, 10) || 0;
      if (now - lastFire < sevenDaysMs) return;
    }

    safeSet(localStorage, LOCAL_WEEKLY_RETURNER_LAST_KEY, String(now));
    publishEvent('weekly_returner', {
      days_since_last_visit: Math.round(daysSince * 10) / 10,
      last_visit_ts: lastVisit,
    });
  }

  // ---- Add-to-cart qualified ----
  // Listens for cart:update events. Fires add_to_cart_qualified when:
  //   1. The event is an add (not a quantity change) — detected via
  //      detail.data.productId existence + absence of didError.
  //   2. The user has ≥30s of cumulative session dwell, filtering misclicks
  //      and bots.
  //   3. The variantId hasn't already qualified this session (dedupe).

  onCartAdd(e) {
    const detail = e && e.detail;
    const data = detail && detail.data;
    if (!data) return;
    if (data.didError) return;
    if (!data.productId) return; // CartUpdateEvent (line item change) — ignore

    const totalDwell = this.totalSessionDwellMs();
    if (totalDwell < 30000) return;

    const variantId = data.variantId ? String(data.variantId) : '';
    const productId = String(data.productId);
    // Dedupe key prefers variantId, falls back to productId
    const dedupeKey = variantId || productId;

    const fired = getSessionList(SESSION_ATC_QUALIFIED_FIRED_KEY);
    if (fired.includes(dedupeKey)) return;
    appendUniqueToSessionList(SESSION_ATC_QUALIFIED_FIRED_KEY, dedupeKey);

    publishEvent('add_to_cart_qualified', {
      product_id: productId,
      variant_id: variantId || null,
      item_count: data.itemCount || 1,
      source: data.source || 'unknown',
      dwell_seconds: Math.round(totalDwell / 1000),
      pages_in_session: parseInt(safeGet(sessionStorage, SESSION_PAGES_COUNT_KEY) || '0', 10) || 0,
    });
  }
}

if (!customElements.get('high-intent-visit-tracking')) {
  customElements.define('high-intent-visit-tracking', HighIntentVisitTracking);
}
